/**
 * Combobox Component
 * 
 * Select editável que permite selecionar opções predefinidas ou digitar valores personalizados.
 * Solicitado pelo cliente para cadastro de receitas, despesas e despesas futuras.
 */
import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  id?: string;
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  /**
   * Callback invoked when the user explicitly selects the "custom value" item from the dropdown.
   * Useful for cases where the typed value should also be persisted in a user-managed list.
   */
  onCustomSelect?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  allowCustom?: boolean;
  customLabel?: string;
  className?: string;
  disabled?: boolean;
  /** Controla se o trigger é um botão (padrão) ou um input único digitável (sem "caixa dupla"). */
  mode?: "button" | "input";
  /** Controla se o Popover deve ser renderizado via Portal. */
  portalled?: boolean;
}

export function Combobox({
  id,
  options,
  value,
  onChange,
  onCustomSelect,
  placeholder = "Selecione...",
  searchPlaceholder = "Buscar...",
  emptyText = "Nenhum item encontrado.",
  allowCustom = true,
  customLabel = "Usar valor digitado",
  className,
  disabled,
  mode = "button",
  portalled = true,
}: Readonly<ComboboxProps>) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [hasUserTypedSinceOpen, setHasUserTypedSinceOpen] = React.useState(false);

  // Find the current option label
  const selectedOption = options.find((option) => option.value === value);
  
  // Check if input value matches any option
  const inputMatchesOption = options.some(
    (option) => option.label.toLowerCase() === inputValue.toLowerCase()
  );

  // Filter options based on input
  // No modo "input", ao abrir queremos exibir a lista completa mesmo com valor pré-preenchido.
  const filterQuery =
    mode === "input" && open && !hasUserTypedSinceOpen ? "" : inputValue;

  const filteredOptions = filterQuery
    ? options.filter((option) =>
        option.label.toLowerCase().includes(filterQuery.toLowerCase())
      )
    : options;

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
    setInputValue("");
  };

  const handleCustomValue = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      onChange(trimmed);
      onCustomSelect?.(trimmed);
      setOpen(false);
      setInputValue("");
    }
  };

  // No modo "input", o campo visível é o único input. Usamos `inputValue` apenas enquanto o popover está aberto.
  React.useEffect(() => {
    if (!open || mode !== "input") {
      if (!open) setInputValue("");
      return;
    }

    // Ao abrir, inicializa o texto com o valor atual.
    setInputValue(selectedOption?.label || value || "");
    setHasUserTypedSinceOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {mode === "input" ? (
          <div className="relative w-full">
            <Input
              id={id}
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              placeholder={placeholder}
              value={open ? inputValue : (selectedOption?.label || value || "")}
              onClick={() => setOpen(true)}
              onChange={(e) => {
                const next = e.target.value;
                if (!open) setOpen(true);
                setInputValue(next);
                setHasUserTypedSinceOpen(true);
                onChange(next);
              }}
              className={cn("pr-9", className)}
            />
            <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
          </div>
        ) : (
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-muted-foreground",
              className
            )}
            disabled={disabled}
          >
            {value ? (selectedOption?.label || value) : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        portalled={portalled}
        // No modo "input", mantemos o foco no input do trigger (evita que o Popover roube o foco
        // e garante digitação contínua sem "quebrar" em testes/UX).
        onOpenAutoFocus={(event) => {
          if (mode === "input") event.preventDefault();
        }}
      >
        <Command shouldFilter={false}>
          {mode !== "input" && (
            <CommandInput
              placeholder={searchPlaceholder}
              value={inputValue}
              onValueChange={setInputValue}
            />
          )}
          <CommandList>
            <CommandEmpty>
              {emptyText}
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
              {/* Option to use custom typed value */}
              {allowCustom &&
                inputValue.trim() &&
                !inputMatchesOption &&
                (mode !== "input" || hasUserTypedSinceOpen) && (
                <CommandItem
                  onSelect={handleCustomValue}
                  className="text-primary"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {customLabel}: "{inputValue}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export type { ComboboxOption };






